import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { useDataContext } from "../../hooks/useData";
import useUserFields from "../../hooks/useUserFields";
import {
  planLeave,
  useCarryOutLeave
} from "../../hooks/useConfirmNavigation";
import {
  destinationOf,
  guardsNavigation
} from "../../hooks/navigationGuard";

// Once for the app, inside the data router (the one blocker of the router):
// the browser's Back and Forward ask to confirm by the rules of the app's
// navigations (useConfirmNavigation: planLeave), when they leave unsaved
// work: the form being edited with changes (first put into the draft when
// the destination stays in its scope: then they go with no question), the
// drafts the destination leaves. The app's own navigations are not blocked
// (guardsNavigation): they asked already, or must not ask.
// The router puts the URL back while it asks. "Exit Without Saving" goes on,
// and the drafts left are discarded once the router is there, not before:
// the variant pages of a page created in the draft would redirect to the
// property first, a navigation that takes the place of the Back.
export default function NavigationGuard() {
  const { store } = useDataContext();
  const { branch } = useUserFields("branch");
  const carryOut = useCarryOutLeave();

  // Read when the browser navigates, outside a render
  const branchRef = useRef(branch);
  branchRef.current = branch;
  const carryOutRef = useRef(carryOut);
  carryOutRef.current = carryOut;

  // The plan of the navigation blocked, the blocker asked about, the
  // discard to run once the router has left
  const planRef = useRef(null);
  const askedRef = useRef(null);
  const leavingRef = useRef(null);

  const shouldBlock = useCallback(
    (navigation) => {
      if (!guardsNavigation(navigation)) return false;

      const { unsavedData, unsavedScope } = store.getState();
      const plan = planLeave({
        destination: destinationOf(navigation.nextLocation),
        formDirty: Boolean(unsavedData),
        formScope: unsavedScope ?? null,
        branch: branchRef.current
      });

      if (plan.action === "go") {
        // Nothing to ask (or the form's changes are in the draft now)
        carryOutRef.current(plan, () => {});
        return false;
      }
      planRef.current = plan;
      return true;
    },
    [store]
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === "unblocked") {
      // The router is where "Exit Without Saving" asked to go
      const discard = leavingRef.current;
      leavingRef.current = null;
      discard?.();
      return;
    }
    if (blocker.state !== "blocked" || askedRef.current === blocker) return;

    askedRef.current = blocker;
    carryOutRef.current(planRef.current, (proceed, discard) => {
      // Another Back meanwhile: that one is asked
      if (askedRef.current !== blocker) return;
      if (!proceed) {
        blocker.reset();
        return;
      }
      leavingRef.current = discard ?? null;
      blocker.proceed();
    });
  }, [blocker]);

  return null;
}
